import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Send, ArrowLeft, CheckCircle2, Shield, Bug, MessageSquare, AlertTriangle, Sparkles } from 'lucide-react';
import { useLang } from '../contexts/LangContext';
import { supportAPI, authAPI } from '../utils/api';

const CATEGORIES = [
  { id: 'general', tr: 'Genel', en: 'General', icon: MessageSquare },
  { id: 'kvkk', tr: 'KVKK / Veri Talebi', en: 'KVKK / Data Request', icon: Shield },
  { id: 'bug', tr: 'Hata Bildirimi', en: 'Bug Report', icon: Bug },
  { id: 'feedback', tr: 'Geri Bildirim / Öneri', en: 'Feedback', icon: Sparkles },
  { id: 'abuse', tr: 'Kötüye Kullanım', en: 'Abuse / Misuse', icon: AlertTriangle },
];

const Contact = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', category: 'general', subject: '', message: '', consent: false,
  });

  // Prefill if logged in
  useEffect(() => {
    authAPI.getMe().then((r) => {
      const u = r.data;
      if (u) setForm((f) => ({ ...f, name: f.name || u.name || '', email: f.email || u.email || '' }));
    }).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error(lang === 'tr' ? 'KVKK aydınlatma metnine onay vermelisiniz.' : 'Please accept the privacy notice.');
      return;
    }
    setSubmitting(true);
    try {
      await supportAPI.submit(form);
      setSubmitted(true);
      toast.success(lang === 'tr' ? 'Mesajınız alındı. En kısa sürede dönüş yapacağız.' : 'Message received. We will get back to you soon.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (lang === 'tr' ? 'Gönderim başarısız' : 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <ContactShell>
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Mesajınız alındı 🙏' : 'Message received'}
          </h2>
          <p className="text-stone-600 mb-2 max-w-md mx-auto">
            {lang === 'tr'
              ? 'KVKK başvurularına 30 gün içinde yanıt verme yasal yükümlülüğümüz vardır. Diğer mesajlara genelde 48 saat içinde dönüş yaparız.'
              : 'KVKK requests are responded to within 30 days by law. Other messages within ~48 hours.'}
          </p>
          <p className="text-xs text-stone-400 mb-6">
            {lang === 'tr' ? 'Yedek iletişim:' : 'Backup contact:'} <a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline">iletisim@hafizim.com</a>
          </p>
          <button
            type="button"
            onClick={() => { setSubmitted(false); setForm((f) => ({ ...f, subject: '', message: '', consent: false })); }}
            className="px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold"
            data-testid="contact-new-message-btn"
          >
            {lang === 'tr' ? 'Yeni mesaj' : 'New message'}
          </button>
        </div>
      </ContactShell>
    );
  }

  return (
    <ContactShell>
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-4" data-testid="contact-back">
        <ArrowLeft className="w-4 h-4" /> {lang === 'tr' ? 'Geri' : 'Back'}
      </button>

      <header className="space-y-2 mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-semibold uppercase tracking-wider">
          <Mail className="w-3 h-3" /> {lang === 'tr' ? 'İletişim' : 'Contact'}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lang === 'tr' ? 'Bize ulaşın' : 'Contact us'}
        </h1>
        <p className="text-stone-600">
          {lang === 'tr'
            ? 'Soru, geri bildirim, hata raporu veya KVKK kapsamındaki veri talepleriniz için aşağıdaki formu kullanın. Hesabınız olmasa da gönderebilirsiniz.'
            : 'Use this form for any question, feedback, bug report, or KVKK/GDPR data request. No account required.'}
        </p>
      </header>

      <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
        {/* Category cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = form.category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setForm((f) => ({ ...f, category: c.id }))}
                data-testid={`contact-category-${c.id}`}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  active ? 'border-teal-600 bg-teal-50 text-teal-900' : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                }`}
              >
                <Icon className={`w-4 h-4 mb-1 ${active ? 'text-teal-700' : 'text-stone-500'}`} />
                <div className="text-xs font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {lang === 'tr' ? c.tr : c.en}
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={lang === 'tr' ? 'Ad Soyad' : 'Name'} required>
            <input
              type="text"
              required
              maxLength={120}
              value={form.name}
              onChange={update('name')}
              data-testid="contact-name"
              className="w-full p-3 rounded-xl border border-stone-200 outline-none focus:border-teal-500"
              placeholder={lang === 'tr' ? 'Adınız' : 'Your name'}
            />
          </Field>
          <Field label={lang === 'tr' ? 'E-posta' : 'Email'} required>
            <input
              type="email"
              required
              value={form.email}
              onChange={update('email')}
              data-testid="contact-email"
              className="w-full p-3 rounded-xl border border-stone-200 outline-none focus:border-teal-500"
              placeholder="you@example.com"
            />
          </Field>
        </div>

        <Field label={lang === 'tr' ? 'Konu' : 'Subject'} required>
          <input
            type="text"
            required
            maxLength={200}
            value={form.subject}
            onChange={update('subject')}
            data-testid="contact-subject"
            className="w-full p-3 rounded-xl border border-stone-200 outline-none focus:border-teal-500"
            placeholder={lang === 'tr' ? 'Mesajınızın konusu' : 'Subject of your message'}
          />
        </Field>

        <Field label={lang === 'tr' ? 'Mesajınız' : 'Message'} required>
          <textarea
            required
            maxLength={5000}
            minLength={10}
            value={form.message}
            onChange={update('message')}
            data-testid="contact-message"
            rows={7}
            className="w-full p-3 rounded-xl border border-stone-200 outline-none focus:border-teal-500 resize-y"
            placeholder={lang === 'tr' ? 'Detaylı yazın... (en az 10 karakter)' : 'Please describe... (min 10 chars)'}
          />
          <p className="text-[11px] text-stone-400 mt-1">{form.message.length}/5000</p>
        </Field>

        <label className="flex items-start gap-2 text-xs text-stone-700 cursor-pointer select-none" data-testid="contact-consent-label">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={update('consent')}
            required
            className="mt-0.5 w-4 h-4 rounded border-stone-300"
            data-testid="contact-consent"
          />
          <span className="leading-snug">
            {lang === 'tr' ? (
              <>
                Bu formu doldurarak, mesajımın yanıtlanabilmesi amacıyla isim ve e-posta adresimin işlenmesine onay veriyorum.
                Detaylar için <Link to="/gizlilik-politikasi" className="text-teal-700 underline font-semibold">Gizlilik Politikası</Link>.
              </>
            ) : (
              <>
                I consent to processing of my name &amp; email to respond to my message. See <Link to="/privacy" className="text-teal-700 underline font-semibold">Privacy Policy</Link>.
              </>
            )}
          </span>
        </label>

        <div className="flex items-center justify-between gap-3 pt-2">
          <p className="text-xs text-stone-500">
            <Mail className="inline w-3 h-3 mr-1" />
            {lang === 'tr' ? 'Yedek:' : 'Backup:'} <a href="mailto:iletisim@hafizim.com" className="text-teal-700 hover:underline">iletisim@hafizim.com</a>
          </p>
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <Send className="w-4 h-4" /> {submitting ? (lang === 'tr' ? 'Gönderiliyor…' : 'Sending…') : (lang === 'tr' ? 'Gönder' : 'Send')}
          </button>
        </div>
      </form>
    </ContactShell>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-stone-700 mb-1.5 uppercase tracking-wider">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

const ContactShell = ({ children }) => (
  <div className="min-h-screen bg-stone-50">
    <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12">{children}</div>
    <footer className="text-center text-xs text-stone-400 py-6 space-x-3">
      <Link to="/" className="hover:underline">© Hafızım</Link>
      <Link to="/gizlilik-politikasi" className="hover:underline">Gizlilik & KVKK</Link>
      <Link to="/kullanim-kosullari" className="hover:underline">Kullanım Koşulları</Link>
    </footer>
  </div>
);

export default Contact;
